import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, FileText, Calendar, User, Building, CreditCard, Eye } from 'lucide-react';
import { budgetStructure, expenseStatusOptions } from './budgetConfig';

const documentTypes = {
  facture: 'Facture',
  bon_commande: 'Bon de commande',
  cheque: 'Chèque',
  justificatif: 'Justificatif',
  contrat: 'Contrat',
  autre: 'Autre'
};

const formatCurrency = (value, currency) => {
  const symbols = { EUR: '€', USD: '$', MAD: 'DH' };
  const symbol = symbols[currency] || currency;
  if (typeof value !== 'number') return `0 ${symbol}`;
  return `${new Intl.NumberFormat('fr-FR').format(value)} ${symbol}`;
};

// @ts-nocheck
export default function ExpenseDetailModal({ isOpen, onClose, expense, users, projectCurrency }) {
  if (!expense) return null;

  const responsibleUser = users?.find(u => u.id === expense.responsible_id);
  const category = budgetStructure.find(c => c.key === expense.category_key);
  const subCategory = category?.subCategories?.find(sc => sc.key === expense.sub_category_key);
  const status = expenseStatusOptions[expense.status];

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Détail de la dépense
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header avec statut */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="text-lg font-semibold">{expense.description}</h3>
              <p className="text-sm text-gray-600">
                {category?.name} → {subCategory?.name}
              </p>
            </div>
            <Badge className={status?.color || 'bg-gray-100 text-gray-800'}>
              {status?.label || 'Inconnu'}
            </Badge>
          </div>

          {/* Informations principales */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informations générales */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Informations générales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Fournisseur:</span>
                    <p className="font-medium">{expense.supplier || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Phase projet:</span>
                    <p className="font-medium">{expense.project_phase || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Date dépense:</span>
                    <p className="font-medium">
                      {expense.expense_date ? new Date(expense.expense_date).toLocaleDateString('fr-FR') : '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">N° BC/Facture:</span>
                    <p className="font-medium">{expense.po_invoice_number || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Centre de coût:</span>
                    <p className="font-medium">{expense.cost_center || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Responsable:</span>
                    <p className="font-medium">{responsibleUser?.full_name || '—'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Informations financières */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Informations financières
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Montant HT:</span>
                    <p className="font-bold text-lg">{formatCurrency(expense.amount_ht, projectCurrency)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Taux TVA:</span>
                    <p className="font-medium">{expense.vat_rate}%</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Montant TTC:</span>
                    <p className="font-bold text-xl text-teal-600">{formatCurrency(expense.amount_ttc, projectCurrency)}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Échéance paiement:</span>
                    <p className="font-medium">
                      {expense.payment_due_date ? new Date(expense.payment_due_date).toLocaleDateString('fr-FR') : '—'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Documents justificatifs */}
          {expense.documents && expense.documents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documents justificatifs ({expense.documents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {expense.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-500" />
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>{documentTypes[doc.type] || 'Autre'}</span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(doc.uploaded_date).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(doc.file_url, doc.name)}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Télécharger
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(!expense.documents || expense.documents.length === 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documents justificatifs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Aucun document associé à cette dépense</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
